import { Box, Heading, Text, VStack, HStack, SimpleGrid, Icon, Button, Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faLayerGroup, faBullhorn, faImage, faUser, faLink, faChartLine, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import MarketingLayout from '../components/marketing/MarketingLayout';
import CodeBlock from '../components/marketing/CodeBlock';

const trackingSnippet = `<!-- StatFalcon Analytics -->
<script>
  (function(w){
    w.StatFalconQueue=w.StatFalconQueue||[];
    w.statfalcon=function(){StatFalconQueue.push(arguments)};
    var s=document.createElement('script');
    s.async=true;
    s.src='https://YOUR_DOMAIN/tracker.js';
    s.dataset.site='YOUR_SITE_UUID';
    document.head.appendChild(s);
  })(window);
</script>`;

const zoneSnippet = `<!-- StatFalcon Ad Zone -->
<div id="statfalcon-zone-1"></div>
<script src="https://YOUR_DOMAIN/serve.js?zone=1" async></script>`;

const concepts = [
  { icon: faGlobe, title: 'Sites', text: 'A website you track. Each site has a unique UUID used by the tracking script and its own API key.' },
  { icon: faLayerGroup, title: 'Zones', text: 'A placement on a site with fixed dimensions where ads are served. Zones can be web or email type.' },
  { icon: faBullhorn, title: 'Campaigns', text: 'A flight of advertising tied to an advertiser, with budget, schedule, priority, targeting, and linked zones.' },
  { icon: faImage, title: 'Ads', text: 'The creative — image, HTML, or video — attached to one or more campaigns, with a destination URL.' },
  { icon: faUser, title: 'Advertisers', text: 'The organizations running campaigns. Performance rolls up per advertiser across all their campaigns.' },
  { icon: faLink, title: 'UTM Links', text: 'Short, trackable redirect links that capture clicks, device, and geo before forwarding to the destination.' },
  { icon: faChartLine, title: 'Tracking', text: 'Cookieless, first-party pageview and event collection — no third-party cookies, no consent banner required.' },
];

const Section = ({ title, children }) => (
  <Box>
    <Heading size="lg" color="gray.900" mt={10} mb={3}>{title}</Heading>
    {children}
  </Box>
);

export const DocumentationPage = () => (
  <MarketingLayout title="Documentation" subtitle="Everything you need to get StatFalcon tracking visitors and serving ads.">
    <VStack align="stretch" spacing={2}>
      <Section title="What is StatFalcon?">
        <Text color="gray.700" lineHeight="1.8">
          StatFalcon is a privacy-first web analytics and ad serving platform in one product. It tracks visitors,
          serves and rotates ads across your sites, and reports on both from a single dashboard — so your traffic
          and revenue numbers always reconcile. Run it in our <strong>Cloud</strong> (zero setup) or
          {' '}<strong>self-host</strong> it on your own infrastructure for complete data ownership.
        </Text>
      </Section>

      <Section title="Quick start">
        <Text color="gray.700" lineHeight="1.8" mb={2}>
          1. Create a <strong>Site</strong> in the dashboard. 2. Add the tracking snippet to your site's
          {' '}<Text as="code" fontFamily="mono">&lt;head&gt;</Text>. 3. Create a <strong>Zone</strong> and paste its
          embed where you want an ad. 4. Create an <strong>Advertiser</strong>, <strong>Campaign</strong>, and
          {' '}<strong>Ad</strong>, then link the campaign to your zone.
        </Text>
      </Section>

      <Section title="Install the tracking code">
        <Text color="gray.700" lineHeight="1.8">
          Copy this into the <Text as="code" fontFamily="mono">&lt;head&gt;</Text> of every page you want to track,
          replacing <Text as="code" fontFamily="mono">YOUR_DOMAIN</Text> with your StatFalcon URL and
          {' '}<Text as="code" fontFamily="mono">YOUR_SITE_UUID</Text> with your site's UUID (shown on the Site's
          tracking-code screen).
        </Text>
        <CodeBlock label="index.html">{trackingSnippet}</CodeBlock>
      </Section>

      <Section title="Embed an ad zone">
        <Text color="gray.700" lineHeight="1.8">
          Each zone has its own embed. Drop this where the ad should appear — <Text as="code" fontFamily="mono">serve.js</Text>
          {' '}fills the placeholder with the highest-priority eligible ad and handles rotation, frequency capping, and
          click tracking automatically.
        </Text>
        <CodeBlock label="HTML">{zoneSnippet}</CodeBlock>
        <Text color="gray.700" lineHeight="1.8">On WordPress, use the shortcode instead:</Text>
        <CodeBlock label="WordPress">{'[statfalcon_zone id="1"]'}</CodeBlock>
        <Text color="gray.600" fontSize="sm">
          For email newsletters, generate a per-ad snippet from the Email Snippet button in the Ads screen — email
          ads use a tracking pixel and click redirect instead of JavaScript.
        </Text>
      </Section>

      <Section title="Core concepts">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} mt={2}>
          {concepts.map((c) => (
            <HStack key={c.title} align="start" spacing={4} p={5} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
              <Box w={10} h={10} bgGradient="linear(135deg, brand.500, accent.500)" borderRadius="lg" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <Icon as={FontAwesomeIcon} icon={c.icon} color="white" />
              </Box>
              <Box>
                <Heading size="sm" color="gray.900" mb={1}>{c.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="1.6">{c.text}</Text>
              </Box>
            </HStack>
          ))}
        </SimpleGrid>
      </Section>

      <Section title="Next steps">
        <HStack spacing={4} flexWrap="wrap" mt={2}>
          <Button as={RouterLink} to="/docs/installation" colorScheme="brand" borderRadius="xl" rightIcon={<FontAwesomeIcon icon={faArrowRight} />}>Installation Guide</Button>
          <Button as={RouterLink} to="/docs/api" variant="outline" borderRadius="xl" borderColor="brand.500" color="brand.600">API Reference</Button>
          <ChakraLink as={RouterLink} to="/contact" color="brand.600" fontWeight="600" alignSelf="center">Need help? Contact us →</ChakraLink>
        </HStack>
      </Section>
    </VStack>
  </MarketingLayout>
);

export default DocumentationPage;
